import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { motion } from 'framer-motion';
import {
    PiArrowRight,
    PiPlay,
    PiCornersOut,
    PiLightning,

    PiStack
} from 'react-icons/pi';

// ============================================
// ANIMATIONS
// ============================================

const rotateSlow = keyframes`
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to { transform: translate(-50%, -50%) rotate(360deg); }
`;



// ============================================
// STYLED COMPONENTS
// ============================================

const HeroSection = styled.section`
    position: relative;
    min-height: 85vh;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background-color: #fdfdfd;
    color: #1a1a1a;
    padding: 100px 24px 100px; /* Reduced top padding, kept generous side/bottom */

    /* Base Pattern: Dot Matrix */
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: radial-gradient(#d1d5db 1px, transparent 1px);
        background-size: 32px 32px;
        mask-image: radial-gradient(circle at center, black 40%, transparent 100%);
        opacity: 0.5;
    }
`;

// Geometric Background Elements
const BackgroundShapes = styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;
`;

const BigCircle = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    width: 80vw;
    height: 80vw;
    max-width: 1200px;
    max-height: 1200px;
    border: 1px dashed rgba(229, 231, 235, 0.8);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    animation: ${rotateSlow} 120s linear infinite;

    &::before {
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 50%;
        border: 1px dashed rgba(138, 43, 226, 0.15);
        transform: rotate(45deg);
    }
`;

const InnerCircle = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    width: 40vw;
    height: 40vw;
    max-width: 600px;
    max-height: 600px;
    border: 1px solid rgba(229, 231, 235, 0.6);
    border-radius: 50%;
    transform: translate(-50%, -50%);
`;

const DecorativeArc = styled.div`
    position: absolute;
    top: 50%;
    right: -10%;
    width: 600px;
    height: 600px;
    border: 1px solid rgba(138, 43, 226, 0.2); /* Purple accent */
    border-radius: 50%;
    border-left-color: transparent;
    border-bottom-color: transparent;
    transform: translate(0, -50%) rotate(-15deg);
`;

const DecorativeCrosses = styled.div`
    position: absolute;
    inset: 0;
    
    &::before {
        content: '+';
        position: absolute;
        top: 20%;
        left: 10%;
        color: #d1d5db;
        font-family: monospace;
    }

    &::after {
        content: '+';
        position: absolute;
        bottom: 20%;
        right: 10%;
        color: #d1d5db;
        font-family: monospace;
    }
`;

const ContentWrapper = styled.div`
    max-width: 1200px;
    width: 100%;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 60px;
    text-align: center;
`;

// CENTERED CONTENT COLUMN
const CenteredContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 800px;
    align-items: center;
`;

const LabelBadge = styled(motion.div)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 100px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.75rem;
    color: #6b7280;
    width: fit-content;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
    
    span {
        width: 6px;
        height: 6px;
        background: linear-gradient(135deg, #8A2BE2, #3B82F6);
        border-radius: 50%;
    }
`;

const MainHeading = styled(motion.h1)`
    font-family: 'Space Grotesk', sans-serif;
    font-size: 4.5rem;
    line-height: 1.1;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.04em;

    span {
        position: relative;
        white-space: nowrap;
        background: linear-gradient(120deg, rgba(138, 43, 226, 0.15) 0%, rgba(138, 43, 226, 0.0) 100%);
        padding: 0 8px;
        margin: 0 -8px;
        border-radius: 4px;
    }

    @media (max-width: 768px) {
        font-size: 3rem;
    }
`;

const Description = styled(motion.p)`
    font-family: 'Inter', sans-serif;
    font-size: 1.25rem;
    line-height: 1.6;
    color: #4b5563;
    max-width: 640px;
`;

const Actions = styled(motion.div)`
    display: flex;
    gap: 16px;
    margin-top: 16px;
    flex-wrap: wrap;
    justify-content: center;
`;

const TechButton = styled.a<{ primary?: boolean }>`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    height: 52px;
    padding: 0 24px;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 1rem;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 6px;
    
    ${props => props.primary ? `
        background: #111827;
        color: #ffffff;
        border: 1px solid #111827;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 20px -10px rgba(0,0,0,0.3);
        }
    ` : `
        background: #ffffff;
        color: #111827;
        border: 1px solid #e5e7eb;

        &:hover {
            border-color: #d1d5db;
            background: #f9fafb;
        }
    `}
`;

// VISUAL CONTAINER (Centered below text)
const VisualContainer = styled.div`
    position: relative;
    height: 450px;
    width: 600px; /* Reduced width for better proportion in center */
    display: flex;
    justify-content: center;
    align-items: center;
    perspective: 2000px;
    margin-top: 20px;

    @media (max-width: 768px) {
        width: 100%;
        height: 350px;
        transform: scale(0.8);
    }
`;

const IsoPlane = styled(motion.div)`
    position: relative;
    width: 460px;
    height: 380px;
    transform-style: preserve-3d;
    transform: rotateX(54deg) rotateZ(-45deg);
`;

const Layer = styled(motion.div) <{ z: number }>`
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid #e5e7eb;
    box-shadow: 
        4px 4px 0px rgba(0,0,0,0.02),
        0 20px 40px rgba(0,0,0,0.05);
    transform: translateZ(${props => props.z}px);
    display: flex;
    flex-direction: column;
    padding: 20px;
    backdrop-filter: blur(4px);
    
    /* Technical corners */
    &::before, &::after {
        content: '';
        position: absolute;
        width: 8px;
        height: 8px;
        border: 1px solid #9ca3af;
        transition: all 0.3s ease;
    }
    &::before { top: 4px; left: 4px; border-right: none; border-bottom: none; }
    &::after { bottom: 4px; right: 4px; border-left: none; border-top: none; }
`;

const LayerLabel = styled.div`
    position: absolute;
    right: -24px;
    bottom: 20px;
    transform: rotate(90deg) origin(100% 100%);
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.6rem;
    color: #9ca3af;
    letter-spacing: 0.1em;
    white-space: nowrap;
`;

const SkeletonBlock = styled.div<{ width?: string; height?: string; highlight?: boolean }>`
    width: ${props => props.width || '100%'};
    height: ${props => props.height || '20px'};
    background: ${props => props.highlight ? 'rgba(138, 43, 226, 0.08)' : '#f3f4f6'};
    border: 1px dashed ${props => props.highlight ? 'rgba(138, 43, 226, 0.25)' : '#e5e7eb'};
    margin-bottom: 12px;
    border-radius: 4px;
`;

const ConnectorLine = styled(motion.div)`
    position: absolute;
    width: 1px;
    height: 160px;
    border-left: 1px dashed #d1d5db;
    left: 50%;
    top: 50%;
    transform: translateZ(20px) translateY(-50%);
    transform-style: preserve-3d;
`;

const AnnotationCard = styled(motion.div) <{ top: string; left: string }>`
    position: absolute;
    top: ${props => props.top};
    left: ${props => props.left};
    background: #ffffff;
    padding: 6px 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 4px;
    
    svg { color: #8A2BE2; }
`;

const TechStats = styled.div`
    display: flex;
    gap: 40px;
    margin-top: 16px;
    padding-top: 32px;
    
    @media (max-width: 640px) {
        flex-direction: column;
        gap: 24px;
    }
`;

const StatItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;

    span:nth-of-type(1) {
        font-family: 'Space Grotesk', sans-serif;
        font-size: 2rem;
        font-weight: 700;
        color: #111827;
    }
    span:nth-of-type(2) {
        font-family: 'Inter', sans-serif;
        font-size: 0.8rem;
        color: #6b7280;
    }
`;

// ============================================
// COMPONENT
// ============================================

const TrustHero: React.FC = () => {
    return (
        <HeroSection>
            {/* New Background Elements */}
            <BackgroundShapes>
                <BigCircle />
                <InnerCircle />
                <DecorativeArc />
                <DecorativeCrosses />
            </BackgroundShapes>

            <ContentWrapper>
                <CenteredContent>
                    <LabelBadge
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <span />
                        SYSTEM V2.0 // EARLY ACCESS
                    </LabelBadge>

                    <MainHeading
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        Blueprint for <br />
                        <span>Intelligent Support.</span>
                    </MainHeading>

                    <Description
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Engineer your perfect customer experience with AI agents that learn, adapt, and scale. No black boxes—just pure, transparent logic on a reliable infrastructure.
                    </Description>

                    <Actions
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                    >
                        <TechButton href="/get-started" primary>
                            Start Building
                            <PiArrowRight />
                        </TechButton>
                        <TechButton href="#demo">
                            <PiPlay />
                            View Architecture
                        </TechButton>
                    </Actions>

                    <TechStats>
                        <StatItem>
                            <span>99.9%</span>
                            <span>Uptime SLA</span>
                        </StatItem>
                        <StatItem>
                            <span>&lt;100ms</span>
                            <span>Latency</span>
                        </StatItem>
                        <StatItem>
                            <span>100%</span>
                            <span>Transparent</span>
                        </StatItem>
                    </TechStats>
                </CenteredContent>

                <VisualContainer>
                    {/* Floating Annotations (Overlay) */}
                    <AnnotationCard
                        top="20%" left="-10%"
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 1 }}
                    >
                        <PiLightning />
                        RESPONSE: 45ms
                    </AnnotationCard>

                    <AnnotationCard
                        top="60%" left="80%"
                        initial={{ x: 20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 1.2 }}
                        style={{ flexDirection: 'row-reverse' }}
                    >
                        <PiStack />
                        LLM: CONNECTED
                    </AnnotationCard>

                    <IsoPlane
                        initial={{ rotateX: 60, rotateZ: -45, scale: 0.8 }}
                        animate={{ rotateX: 54, rotateZ: -45, scale: 1 }}
                        transition={{ duration: 1, ease: "easeOut" }}
                    >
                        {/* BOTTOM LAYER: DATA */}
                        <Layer
                            z={0}
                            initial={{ opacity: 0, z: -50 }}
                            animate={{ opacity: 1, z: 0 }}
                            transition={{ delay: 0.4, duration: 0.5 }}
                        >
                            <LayerLabel>LAYER_01: INFRASTRUCTURE</LayerLabel>
                            <SkeletonBlock height="100%" width="100%" />
                        </Layer>

                        {/* MIDDLE LAYER: LOGIC */}
                        <Layer
                            z={60}
                            initial={{ opacity: 0, z: 0 }}
                            animate={{ opacity: 1, z: 60 }}
                            transition={{ delay: 0.6, duration: 0.5 }}
                        >
                            <LayerLabel>LAYER_02: AI_LOGIC</LayerLabel>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', height: '100%' }}>
                                <SkeletonBlock height="100%" highlight />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <SkeletonBlock height="40px" />
                                    <SkeletonBlock height="40px" />
                                    <SkeletonBlock height="100%" />
                                </div>
                            </div>
                        </Layer>

                        {/* TOP LAYER: UI */}
                        <Layer
                            z={120}
                            initial={{ opacity: 0, z: 0 }}
                            animate={{ opacity: 1, z: 120 }}
                            transition={{ delay: 0.8, duration: 0.5 }}
                            style={{ background: 'rgba(255, 255, 255, 0.95)' }}
                        >
                            <LayerLabel style={{ color: '#111827' }}>LAYER_03: INTERFACE</LayerLabel>
                            <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '12px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f87171' }}></div>
                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fbbf24' }}></div>
                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#34d399' }}></div>
                                </div>
                                <PiCornersOut size={12} color="#9ca3af" />
                            </div>
                            <div style={{ display: 'flex', gap: '12px', height: '100%' }}>
                                <SkeletonBlock width="60px" height="100%" />
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <SkeletonBlock height="60px" />
                                    <SkeletonBlock height="30px" />
                                    <SkeletonBlock height="100px" style={{ marginTop: 'auto', background: '#f3f4f6' }} />
                                </div>
                            </div>
                        </Layer>

                        {/* Connector */}
                        <ConnectorLine
                            initial={{ height: 0 }}
                            animate={{ height: 160 }}
                            transition={{ delay: 1, duration: 0.5 }}
                        />

                    </IsoPlane>
                </VisualContainer>
            </ContentWrapper>
        </HeroSection>
    );
};

export default TrustHero;
